import { api, Product, responseErrorCheck, TmrBaseResource, TmrProduct } from 'stylewhere/api'
import { config } from 'stylewhere/config'
import { AppStore } from 'stylewhere/shared'
import Images from './Images'

export class Products extends TmrBaseResource {
  static readonly endpoint = 'api/v1/products' //tracking

  static searchCodes(codes: string[]) {
    return this.search<TmrProduct>({ equalCodes: codes })
  }

  static async getProductsByCodes(upcs: string[]): Promise<Product[]> {
    return api
      .post<Product>(`${this.endpoint}/advancedSearch`, {
        codes: upcs,
        limit: 50000
      })
      .then(responseErrorCheck)
  }

  private static readonly getImageIdFromAttributes = (attributes: any): string => {
    if (attributes?.pictureId?.value) return attributes.pictureId.value
    if (attributes?.sketchId?.value) return attributes.sketchId.value
    if (attributes?.imageId?.value) return attributes.imageId.value
    return undefined
  }

  private static readonly getImageIdFromLegacyFields = (record: any): string => {
    if (record._ImageId) return record._ImageId
    if (record._SkatchId) return record._SkatchId
    return undefined
  }

  private static readonly getImageIdFromCode = (record: any): string => {
    if (record.code) return `IMG-${record.code}`
    if (record.productCode) return `IMG-${record.productCode}`
    return undefined
  }

  static readonly getImageID = (record: any) => {
    if (!record) return ''

    let imageId = this.getImageIdFromAttributes(record.attributes) || this.getImageIdFromLegacyFields(record)

    if (!imageId) {
      imageId = this.getImageIdFromCode(record)
    }

    return imageId
  }

  static readonly isProductImage = (image: string): boolean => {
    return image.includes('http') && image.includes('product-images')
  }

  static readonly parseImageUrl = (publicUrl: string, size: string): string => {
    try {
      if (!publicUrl.includes('{')) {
        // Pulisce la stringa rimuovendo tutto quello che viene dopo " - "
        const cleanedUrl = publicUrl.includes(' - ') ? publicUrl.substring(0, publicUrl.indexOf(' - ')) : publicUrl
        return cleanedUrl
      }

      const lastBraceIndex = publicUrl.lastIndexOf('}')
      const cleanedUrl = lastBraceIndex !== -1 ? publicUrl.substring(0, lastBraceIndex + 1) : publicUrl

      const decode = JSON.parse(cleanedUrl)
      if (decode?.imageUrls?.[size || 'large']) {
        return decode.imageUrls[size || 'large']
      }
    } catch (_e) {
      console.error(_e)
    }
    return ''
  }

  static readonly extractImageIdFromImage = (image: string): string => {
    if (image.includes('http')) {
      const match = image.match(/([a-f0-9-]{36})(?:\.\w+)?$/i)
      if (match) {
        return match[1]
      }
    }
    return image
  }

  private static readonly buildPrivateImageUrl = (imageId: string, type: string): string => {
    const baseURL = config.endpoint
    return `${baseURL}${Images.endpoint}?imageId=${encodeURIComponent(decodeURIComponent(imageId))}&access_token=${AppStore.authToken}&size=${type}`
  }

  private static readonly getPublicImageUrl = (product: any, type: string): string => {
    let publicUrl = ''
    if (product.attributes?.imageId?.value) {
      publicUrl = product.attributes.imageId.value
    } else if (product._ImageId) {
      publicUrl = product._ImageId.value || product._ImageId
    }

    if (!publicUrl) return ''

    try {
      const decode = JSON.parse(publicUrl)
      return decode?.imageUrls?.[type] || ''
    } catch (e) {
      console.error(e)
      return ''
    }
  }

  static readonly getImageUrl = (product?: any, type = 'medium') => {
    if (!product || !AppStore.authToken) {
      return ''
    }

    const settings = AppStore.getShowProductImageSettings()
    const isPrivateUrl = settings === 'StylewherePrivateUrl' || settings.toLowerCase() === 'yes'

    if (isPrivateUrl) {
      const imageId = this.getImageID(product)
      const imageUrl = this.parseImageUrl(imageId, type)

      if (imageId && imageId !== '' && !this.isProductImage(imageId)) {
        return this.buildPrivateImageUrl(imageId, type)
      }
      return imageUrl
    }

    return this.getPublicImageUrl(product, type)
  }
}
